import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataFrame } from './data/DataFrame';
import {
  Badge,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Pagination,
  SegmentedControl,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
  Toggle,
} from '@cloudscape-design/components';
import {
  PageSkeleton,
  EmptyState,
  useAuth,
  useRoles,
  useNotify,
  createApiClient,
} from '@deloitte-us-consulting/gp-sdk';

type FieldClass = 'unclassified' | 'standard' | 'restricted';

interface CatalogField {
  fieldId: string;
  name: string;
  type: string;
  class: FieldClass;
  description?: string;
  status: 'active' | 'missing';
  needsReview?: boolean;
}

interface FieldCounts {
  total: number;
  standard: number;
  restricted: number;
  unclassified: number;
  missing: number;
}

interface SourceSummary {
  connector?: string;
  sourceId: string;
  displayName: string;
  description: string;
  summary: string;
  baseId: string;
  tableId: string;
  view?: string;
  defaultClass: 'standard' | 'restricted';
  enabled: boolean;
  refreshedAt?: string;
  updatedAt: string;
  updatedBy: string;
  fieldCounts: FieldCounts;
}

interface SourceDetail extends Omit<SourceSummary, 'fieldCounts'> {
  fields: CatalogField[];
}

interface RefreshChanges {
  added: string[];
  removed: string[];
  restored: string[];
  renamed: { fieldId: string; from: string; to: string }[];
  typeChanged: { fieldId: string; from: string; to: string }[];
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';
const PAGE_SIZE = 25;

/** Save `data` as a pretty-printed JSON file the browser downloads. */
export function downloadJson(filename: string, data: unknown): void {
  const url = URL.createObjectURL(new Blob([`${JSON.stringify(data, null, 2)}
`], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function countFields(fields: CatalogField[]): FieldCounts {
  const counts: FieldCounts = { total: fields.length, standard: 0, restricted: 0, unclassified: 0, missing: 0 };
  for (const f of fields) {
    if (f.status === 'missing') counts.missing++;
    else counts[f.class]++;
  }
  return counts;
}

/** A one-line, human summary of what a refresh found. */
export function describeChanges(c: RefreshChanges): string {
  const parts: string[] = [];
  if (c.added.length) parts.push(`${c.added.length} new (hidden until you classify them)`);
  if (c.removed.length) parts.push(`${c.removed.length} no longer in AirTable`);
  if (c.restored.length) parts.push(`${c.restored.length} back in AirTable`);
  if (c.renamed.length) parts.push(`${c.renamed.length} renamed`);
  if (c.typeChanged.length) parts.push(`${c.typeChanged.length} changed type (review)`);
  return parts.length ? parts.join(', ') : 'No changes in AirTable.';
}

export type FieldFilter = 'all' | 'unclassified' | 'standard' | 'restricted' | 'missing' | 'review';

export function filterFields(fields: CatalogField[], text: string, filter: FieldFilter): CatalogField[] {
  const q = text.trim().toLowerCase();
  return fields.filter((f) => {
    if (q && !f.name.toLowerCase().includes(q)) return false;
    switch (filter) {
      case 'all':
        return true;
      case 'missing':
        return f.status === 'missing';
      case 'review':
        return f.needsReview === true;
      default:
        return f.status === 'active' && f.class === filter;
    }
  });
}

const FILTER_OPTIONS: { value: FieldFilter; label: string }[] = [
  { value: 'all', label: 'All fields' },
  { value: 'unclassified', label: 'Hidden (unclassified)' },
  { value: 'standard', label: 'Standard' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'missing', label: 'Missing from AirTable' },
  { value: 'review', label: 'Type changed' },
];

const accessBadge = (cls: 'standard' | 'restricted') =>
  cls === 'restricted' ? <Badge color="red">Restricted</Badge> : <Badge color="green">Standard</Badge>;

/** A field's description, saved when the admin leaves the box. */
function DescriptionCell({ field, onSave }: { field: CatalogField; onSave: (value: string | null) => void }) {
  const [value, setValue] = useState(field.description ?? '');
  useEffect(() => setValue(field.description ?? ''), [field.description]);
  return (
    <Input
      value={value}
      ariaLabel={`Description for ${field.name}`}
      placeholder="Optional"
      onChange={({ detail }) => setValue(detail.value)}
      onBlur={() => {
        const next = value.trim();
        if (next !== (field.description ?? '')) onSave(next === '' ? null : next);
      }}
    />
  );
}

/**
 * Admin dashboard for the data source catalog (ADR 0031): which sources the
 * platform offers builders, and how each field is exposed. New AirTable columns
 * arrive hidden and stay hidden until classified here. Gated to platform admins;
 * the API enforces the same gate server-side.
 */
export default function DataCatalogAdmin({ embedded = false, systemId }: { embedded?: boolean; systemId?: string } = {}) {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Data:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [sources, setSources] = useState<SourceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SourceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [text, setText] = useState('');
  const [filter, setFilter] = useState<FieldFilter>('all');
  const [page, setPage] = useState(1);

  const loadSources = useCallback(async () => {
    setSources(await api.get<SourceSummary[]>('/api/catalog/sources'));
  }, [api]);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await api.get<SourceSummary[]>('/api/catalog/sources');
        if (!cancelled) setSources(list);
      } catch (e) {
        if (!cancelled) notify({ type: 'error', header: 'Could not load the data catalog', content: (e as Error).message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once. Intentionally NOT depending on `notify` — it is a fresh function
    // each render and would re-run this effect and clobber in-progress edits.
  }, [api, isAllowed]);

  const select = async (sourceId: string) => {
    setSelectedId(sourceId);
    setDetail(null);
    setText('');
    setFilter('all');
    setPage(1);
    setDetailLoading(true);
    try {
      setDetail(await api.get<SourceDetail>(`/api/catalog/sources/${sourceId}`));
    } catch (e) {
      notify({ type: 'error', header: 'Could not load the source', content: (e as Error).message });
    } finally {
      setDetailLoading(false);
    }
  };

  if (!isAllowed) {
    return (
      <DataFrame embedded={embedded} title="Tables and fields">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </DataFrame>
    );
  }
  if (loading) {
    return (
      <DataFrame embedded={embedded} title="Tables and fields">
        <PageSkeleton label="Loading the data catalog…" />
      </DataFrame>
    );
  }

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post<{ created: string[]; skipped: string[]; failed: { sourceId: string; error: string }[] }>(
        '/api/catalog/launch-sources',
      );
      await loadSources();
      if (r.failed.length) {
        notify({
          type: 'error',
          header: `${r.failed.length} launch source${r.failed.length === 1 ? '' : 's'} failed`,
          content: r.failed.map((f) => `${f.sourceId}: ${f.error}`).join('; '),
        });
      } else {
        notify({ type: 'success', header: 'Launch sources added', content: `${r.created.length} added, ${r.skipped.length} already there.` });
      }
    } catch (e) {
      notify({ type: 'error', header: 'Could not add launch sources', content: (e as Error).message });
    } finally {
      setSeeding(false);
    }
  };

  const syncSummary = (d: SourceDetail) =>
    setSources((list) => (list ?? []).map((s) => (s.sourceId === d.sourceId ? { ...s, ...d, fieldCounts: countFields(d.fields) } : s)));

  const setClass = async (field: CatalogField, cls: FieldClass) => {
    if (!detail || cls === field.class) return;
    try {
      const saved = await api.put<CatalogField>(`/api/catalog/sources/${detail.sourceId}/fields/${field.fieldId}`, { class: cls });
      const next = { ...detail, fields: detail.fields.map((f) => (f.fieldId === saved.fieldId ? saved : f)) };
      setDetail(next);
      syncSummary(next);
    } catch (e) {
      notify({ type: 'error', header: `Could not update ${field.name}`, content: (e as Error).message });
    }
  };

  const patchField = async (field: CatalogField, patch: { description?: string | null; needsReview?: false }) => {
    if (!detail) return;
    try {
      const saved = await api.put<CatalogField>(`/api/catalog/sources/${detail.sourceId}/fields/${field.fieldId}`, patch);
      setDetail({ ...detail, fields: detail.fields.map((f) => (f.fieldId === saved.fieldId ? saved : f)) });
    } catch (e) {
      notify({ type: 'error', header: `Could not update ${field.name}`, content: (e as Error).message });
    }
  };

  const refresh = async () => {
    if (!detail) return;
    setRefreshing(true);
    try {
      const r = await api.post<{ source: SourceDetail; changes: RefreshChanges }>(`/api/catalog/sources/${detail.sourceId}/refresh`);
      setDetail(r.source);
      syncSummary(r.source);
      notify({ type: 'success', header: 'Refreshed from AirTable', content: describeChanges(r.changes) });
    } catch (e) {
      notify({ type: 'error', header: 'Refresh failed', content: (e as Error).message });
    } finally {
      setRefreshing(false);
    }
  };

  // Dummy data for builders, from field names and types only. Saved into
  // templates/app/src/sample-data/ so it ships with the app template.
  const downloadSample = async () => {
    if (!detail) return;
    setDownloading(true);
    try {
      const sample = await api.get<unknown>(`/api/catalog/sources/${detail.sourceId}/sample`);
      downloadJson(`${detail.sourceId}.json`, sample);
      notify({
        type: 'success',
        header: 'Sample data downloaded',
        content: `Save ${detail.sourceId}.json into templates/app/src/sample-data/ and commit it. Hidden fields are left out.`,
      });
    } catch (e) {
      notify({ type: 'error', header: 'Could not generate the sample', content: (e as Error).message });
    } finally {
      setDownloading(false);
    }
  };

  const saveDetails = async () => {
    if (!detail) return;
    setSavingDetails(true);
    try {
      const saved = await api.put<SourceDetail>(`/api/catalog/sources/${detail.sourceId}`, {
        displayName: detail.displayName,
        description: detail.description,
        summary: detail.summary,
        enabled: detail.enabled,
      });
      setDetail(saved);
      syncSummary(saved);
      notify({ type: 'success', header: 'Source saved', content: saved.displayName });
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSavingDetails(false);
    }
  };

  const editDetail = (patch: Partial<SourceDetail>) => setDetail((d) => (d ? { ...d, ...patch } : d));

  const list = (sources ?? []).filter((s) => !systemId || s.connector === systemId);
  const visible = detail ? filterFields(detail.fields, text, filter) : [];
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = visible.slice((Math.min(page, pageCount) - 1) * PAGE_SIZE, Math.min(page, pageCount) * PAGE_SIZE);

  return (
    <DataFrame embedded={embedded}
      title="Tables and fields"
      description="The tables builders can connect to, and which fields each exposes. New AirTable columns stay hidden until you classify them. Changes apply on the next request."
    >
      <SpaceBetween size="l">
        {list.length === 0 ? (
          <Container header={<Header variant="h2">Sources</Header>}>
            <Box textAlign="center" padding="l">
              <SpaceBetween size="s">
                <Box variant="strong">No sources yet</Box>
                <Box color="text-body-secondary">
                  Add the launch sources (the CONTRACTS tables, KPIs, Contacts and Liaison GTM) to start. Their fields are read from AirTable.
                </Box>
                <Button variant="primary" loading={seeding} onClick={seed}>
                  Add launch sources
                </Button>
              </SpaceBetween>
            </Box>
          </Container>
        ) : (
          <Table
            variant="container"
            header={
              <Header
                variant="h2"
                counter={`(${list.length})`}
                actions={
                  <Button loading={seeding} onClick={seed}>
                    Add missing launch sources
                  </Button>
                }
              >
                Sources
              </Header>
            }
            items={list}
            trackBy="sourceId"
            selectionType="single"
            selectedItems={list.filter((s) => s.sourceId === selectedId)}
            onSelectionChange={({ detail: d }) => {
              const s = d.selectedItems[0];
              if (s) void select(s.sourceId);
            }}
            ariaLabels={{
              selectionGroupLabel: 'Sources',
              itemSelectionLabel: (_, item) => `Select ${item.displayName}`,
              allItemsSelectionLabel: () => 'Sources',
            }}
            columnDefinitions={[
              { id: 'name', header: 'Source', cell: (s) => s.displayName, isRowHeader: true },
              { id: 'access', header: 'Default access', cell: (s) => accessBadge(s.defaultClass) },
              {
                id: 'fields',
                header: 'Fields',
                cell: (s) =>
                  `${s.fieldCounts.standard} standard · ${s.fieldCounts.restricted} restricted · ${s.fieldCounts.unclassified} hidden`,
              },
              {
                id: 'status',
                header: 'Status',
                cell: (s) =>
                  !s.enabled ? (
                    <StatusIndicator type="stopped">Disabled</StatusIndicator>
                  ) : s.fieldCounts.missing > 0 ? (
                    <StatusIndicator type="warning">{s.fieldCounts.missing} missing in AirTable</StatusIndicator>
                  ) : (
                    <StatusIndicator type="success">Active</StatusIndicator>
                  ),
              },
              { id: 'refreshed', header: 'Last refreshed', cell: (s) => (s.refreshedAt ? new Date(s.refreshedAt).toLocaleString() : '—') },
            ]}
          />
        )}

        {selectedId ? (
          detailLoading || !detail ? (
            <PageSkeleton label="Loading source…" />
          ) : (
            <SpaceBetween size="l">
              <Container
                header={
                  <Header
                    variant="h2"
                    description={`${detail.baseId} · ${detail.tableId}${detail.view ? ` · view: ${detail.view}` : ''}`}
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button loading={downloading} onClick={downloadSample}>
                          Download sample data
                        </Button>
                        <Button loading={refreshing} onClick={refresh}>
                          Refresh from AirTable
                        </Button>
                        <Button variant="primary" loading={savingDetails} onClick={saveDetails}>
                          Save details
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    {detail.displayName}
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <Toggle checked={detail.enabled} onChange={({ detail: d }) => editDetail({ enabled: d.checked })}>
                    Offered to builders
                  </Toggle>
                  <FormField label="Name">
                    <Input value={detail.displayName} onChange={({ detail: d }) => editDetail({ displayName: d.value })} />
                  </FormField>
                  <FormField label="Description" description="What this source is, in plain language. Builders see this while building.">
                    <Input value={detail.description} onChange={({ detail: d }) => editDetail({ description: d.value })} />
                  </FormField>
                  <FormField label="What's in it" description="One line summarising the contents. Shown instead of a field list.">
                    <Input value={detail.summary} onChange={({ detail: d }) => editDetail({ summary: d.value })} />
                  </FormField>
                </SpaceBetween>
              </Container>

              <Table
                variant="container"
                header={
                  <Header
                    variant="h2"
                    counter={`(${visible.length}${visible.length === detail.fields.length ? '' : ` of ${detail.fields.length}`})`}
                    description="Hidden fields are served to nobody. Restricted fields are only granted by request."
                  >
                    Fields
                  </Header>
                }
                items={pageItems}
                trackBy="fieldId"
                filter={
                  <SpaceBetween direction="horizontal" size="xs">
                    <TextFilter
                      filteringText={text}
                      filteringPlaceholder="Find a field"
                      filteringAriaLabel="Find a field"
                      onChange={({ detail: d }) => {
                        setText(d.filteringText);
                        setPage(1);
                      }}
                    />
                    <Select
                      selectedOption={FILTER_OPTIONS.find((o) => o.value === filter) ?? FILTER_OPTIONS[0]!}
                      options={FILTER_OPTIONS}
                      ariaLabel="Filter fields"
                      onChange={({ detail: d }) => {
                        setFilter((d.selectedOption.value as FieldFilter) ?? 'all');
                        setPage(1);
                      }}
                    />
                  </SpaceBetween>
                }
                pagination={
                  <Pagination
                    currentPageIndex={Math.min(page, pageCount)}
                    pagesCount={pageCount}
                    onChange={({ detail: d }) => setPage(d.currentPageIndex)}
                  />
                }
                empty={<Box textAlign="center" color="text-body-secondary">No fields match.</Box>}
                columnDefinitions={[
                  { id: 'name', header: 'Field', cell: (f) => f.name, isRowHeader: true },
                  { id: 'type', header: 'Type', cell: (f) => f.type },
                  {
                    id: 'class',
                    header: 'Exposure',
                    cell: (f) => (
                      <SegmentedControl
                        selectedId={f.class}
                        label={`Exposure for ${f.name}`}
                        options={[
                          { id: 'unclassified', text: 'Hidden' },
                          { id: 'standard', text: 'Standard' },
                          { id: 'restricted', text: 'Restricted' },
                        ]}
                        onChange={({ detail: d }) => void setClass(f, d.selectedId as FieldClass)}
                      />
                    ),
                  },
                  {
                    id: 'notes',
                    header: 'Notes',
                    cell: (f) =>
                      f.status === 'missing' ? (
                        <StatusIndicator type="warning">Missing from AirTable</StatusIndicator>
                      ) : f.needsReview ? (
                        <SpaceBetween direction="horizontal" size="xs">
                          <StatusIndicator type="warning">Type changed</StatusIndicator>
                          <Button variant="inline-link" onClick={() => void patchField(f, { needsReview: false })}>
                            Mark reviewed
                          </Button>
                        </SpaceBetween>
                      ) : (
                        ''
                      ),
                  },
                  {
                    id: 'description',
                    header: 'Description',
                    cell: (f) => <DescriptionCell field={f} onSave={(value) => void patchField(f, { description: value })} />,
                    minWidth: 220,
                  },
                ]}
              />
            </SpaceBetween>
          )
        ) : null}
      </SpaceBetween>
    </DataFrame>
  );
}
